import { HealthBucket } from '@Generated/back-office-client/src'
import { Box, Card, Flex, Heading, Text } from '@radix-ui/themes'
import { formatInTimeZone } from 'date-fns-tz'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts'

const ERROR_COLOR = 'var(--red-9)'
const OTHER_COLOR = 'var(--gray-9)'
const CHART_HEIGHT = 220

type TimelinePoint = {
  start: Date
  error: number
  other: number
}

const TimelineTooltip = ({
  active,
  payload,
  timezone,
  bucketMinutes,
}: TooltipContentProps & { timezone: string; bucketMinutes: number }) => {
  if (!active || !payload?.length) {
    return null
  }
  const point = payload[0].payload as TimelinePoint
  const end = new Date(point.start.getTime() + bucketMinutes * 60 * 1000)

  return (
    <Card size="1">
      <Flex direction="column" gap="1">
        <Text size="2" weight="bold">
          {formatInTimeZone(point.start, timezone, 'p')} – {formatInTimeZone(end, timezone, 'p')}
        </Text>
        <Text size="2">Total: {point.error + point.other}</Text>
        <Text size="2">Errors: {point.error}</Text>
      </Flex>
    </Card>
  )
}

/**
 * Stacked bars of errors against everything else per time slot, so the start and end of an outage are visible.
 */
export const HealthTimeline = ({
  buckets,
  bucketMinutes,
  timezone,
  otherLabel,
}: {
  buckets: HealthBucket[]
  bucketMinutes: number
  timezone: string
  /** Legend name for everything that isn't an error. */
  otherLabel: string
}) => {
  const data: TimelinePoint[] = buckets.map((bucket) => ({
    start: new Date(bucket.start),
    error: bucket.error,
    other: bucket.total - bucket.error,
  }))

  return (
    <Flex direction="column" gap="2">
      <Heading size="4">Over time</Heading>
      <Box height={`${CHART_HEIGHT}px`}>
        <ResponsiveContainer
          width="100%"
          height={CHART_HEIGHT}
          initialDimension={{ width: 800, height: CHART_HEIGHT }}>
          <BarChart data={data} barCategoryGap={2}>
            <CartesianGrid vertical={false} stroke="var(--gray-4)" />
            <XAxis
              dataKey="start"
              tickFormatter={(start: Date) => formatInTimeZone(start, timezone, 'p')}
              tick={{ fill: 'var(--gray-11)', fontSize: 12 }}
              stroke="var(--gray-6)"
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: 'var(--gray-11)', fontSize: 12 }}
              stroke="var(--gray-6)"
              width={40}
            />
            <Tooltip
              cursor={{ fill: 'var(--gray-a3)' }}
              content={(props) => (
                <TimelineTooltip {...props} timezone={timezone} bucketMinutes={bucketMinutes} />
              )}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--gray-11)' }} />
            <Bar
              dataKey="other"
              name={otherLabel}
              stackId="outcomes"
              fill={OTHER_COLOR}
              stroke="var(--color-background)"
              strokeWidth={1}
              isAnimationActive={false}
            />
            <Bar
              dataKey="error"
              name="Errors"
              stackId="outcomes"
              fill={ERROR_COLOR}
              stroke="var(--color-background)"
              strokeWidth={1}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Flex>
  )
}
